import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TransferService from "../services/TransferService";
import AuthService from "../services/AuthService"; // Import du service AuthService
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import moment from 'moment';
import 'moment/locale/fr';
import RoleBasedRender from './RoleBasedRender';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import {
    faClock,
    faUser,
    faEnvelope,
    faPaperPlane,
    faCheck,
    faTimes,
    faUniversity,
    faCreditCard,
    faMoneyBillAlt,
    faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';

const TransferList = ({ transfers, onTransferClick, onCopyReference, onRefresh }) => {
    const [loadingButton, setLoadingButton] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [selectedTransferForReject, setSelectedTransferForReject] = useState(null);
    const [isSubmittingReject, setIsSubmittingReject] = useState(false);

    moment.locale('fr');

    const getMethodIcon = (method) => {
        switch(method.toLowerCase()) {
            case 'bank':
                return faUniversity;
            case 'card':
                return faCreditCard;
            case 'cash':
                return faMoneyBillAlt;
            default:
                return faExchangeAlt;
        }
    };

    const handleCopyClick = (e, referenceNumber) => {
        e.stopPropagation();
        navigator.clipboard.writeText(referenceNumber).then(() => {
            toast.success('Référence copiée dans le presse-papiers');
            onCopyReference();
        }).catch((err) => {
            console.error('Échec de la copie du texte : ', err);
        });
    };

    const handleValidateTransfer = async (e, transfer) => {
        e.stopPropagation();

        // Récupérer l'utilisateur connecté
        const currentUser = AuthService.getCurrentUser();
        if (!currentUser) {
            toast.error('Utilisateur non connecté.');
            return;
        }

        const result = await Swal.fire({
            title: 'Confirmer la validation',
            text: `Êtes-vous sûr de vouloir valider ce transfert de ${transfer.transactionAmount} CAD ?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Oui, valider',
            cancelButtonText: 'Annuler'
        });

        if (!result.isConfirmed) {
            return;
        }

        setLoadingButton(transfer.id);
        try {
            // Ajouter le lastModifiedBy avec le nom de l'utilisateur connecté
            const transferData = {
                ...transfer,
                lastModifiedBy: currentUser.username
            };

            // Afficher les données envoyées au backend
            ////console.log('Données envoyées pour la validation:', JSON.stringify(transferData, null, 2));

            const response = await TransferService.validateTransfer(transferData);
            toast.success('Transfert validé avec succès');
            ////console.log('Réponse du serveur après validation :', response);
            onRefresh();
        } catch (error) {
            toast.error('Erreur lors de la validation du transfert');
            console.error('Erreur lors de la validation du transfert :', error);
        } finally {
            setLoadingButton(null);
        }
    };

    const handleRejectTransfer = (e, transfer) => {
        e.stopPropagation();
        setSelectedTransferForReject(transfer);
        setShowRejectModal(true);
    };

    const handleRejectSubmit = async () => {
        if (!rejectReason || !rejectReason.trim()) {
            toast.error('Veuillez fournir une raison pour rejeter le transfert.');
            return;
        }

        // Récupérer l'utilisateur connecté
        const currentUser = AuthService.getCurrentUser();
        if (!currentUser) {
            toast.error('Utilisateur non connecté.');
            return;
        }

        setIsSubmittingReject(true);
        try {
            // Ajouter le lastModifiedBy avec le nom de l'utilisateur connecté
            const transferData = {
                ...selectedTransferForReject,
                rejectReason: rejectReason.trim(),
                lastModifiedBy: currentUser.username
            };

            // Afficher les données envoyées au backend
            ////console.log('Données envoyées pour le rejet:', JSON.stringify(transferData, null, 2));

            const response = await TransferService.rejectTransfer(transferData);
            toast.success('Transfert rejeté avec succès');
            ////console.log('Réponse du serveur après rejet :', response);
            onRefresh();
            setShowRejectModal(false);
            setRejectReason('');
        } catch (error) {
            toast.error('Erreur lors du rejet du transfert');
            console.error('Erreur lors du rejet du transfert :', error);
        } finally {
            setIsSubmittingReject(false);
        }
    };

    const handleCloseRejectModal = () => {
        setShowRejectModal(false);
        setRejectReason('');
    };

    const formatDate = (dateString) => {
        return moment(dateString).format('DD MMMM YYYY');
    };

    const formatDateTime = (dateString) => {
        return moment(dateString).format('DD MMMM YYYY HH:mm');
    };

    const groupTransfersByDate = (transfers) => {
        return transfers.reduce((acc, transfer) => {
            const date = formatDate(transfer.createdAt);
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(transfer);
            return acc;
        }, {});
    };

    const groupedTransfers = groupTransfersByDate(transfers);

    if (transfers.length === 0) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <img
                    src="assets/images/setting/no_data.png"
                    alt="Aucun transfert"
                    className="mb-3"
                    style={{ width: '100px', opacity: 0.7 }}
                />
                <p className="text-muted">Aucun transfert en attente n'est disponible</p>
            </div>
        );
    }

    return (
        <div className="transfer-list">
            {Object.keys(groupedTransfers).map((date) => (
                <div key={date}>
                    {/* Date Header */}
                    <div className="d-flex align-items-center my-4">
                        <hr className="flex-grow-1 border-top border-2 border-secondary opacity-25" />
                        <span className="px-3 py-1 bg-light rounded-pill fw-bold" style={{ fontSize: '0.85rem' }}>
                            {date}
                        </span>
                        <hr className="flex-grow-1 border-top border-2 border-secondary opacity-25" />
                    </div>

                    {groupedTransfers[date].map((transfer, index) => (
                        <div
                            key={index}
                            className="card mb-3 shadow-sm border-0"
                            onClick={() => onTransferClick(transfer)}
                            style={{
                                cursor: 'pointer',
                                borderRadius: '12px',
                                overflow: 'hidden'
                            }}
                        >
                            <div className="card-body">
                                {/* Header with date and method */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <small className="text-muted">
                                        <FontAwesomeIcon icon={faClock} className="me-1" />
                                        {formatDateTime(transfer.createdAt)}
                                    </small>
                                    <span className="badge bg-light">
                                        <FontAwesomeIcon
                                            icon={getMethodIcon(transfer.withdrawalMethod)}
                                            className="me-1"
                                        />
                                        {transfer.withdrawalMethod}
                                    </span>
                                </div>

                                {/* Amount and status */}
                                <div
                                    className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                                    <h4 className="mb-0 text-warning fw-bold">
                                        {transfer.transactionAmount} CAD
                                    </h4>

                                    <span className={`badge bg-${transfer.transactionStatus === 'PENDING' ? 'warning' : 'success'} text-white`}>
                                        {transfer.transactionStatus === 'PENDING' ? 'En attente' : transfer.transactionStatus}
                                    </span>
                                </div>

                                {/* Transfer details */}
                                <div className="mb-3">

                                    <div className="d-flex align-items-start mb-2">
                                        <FontAwesomeIcon
                                            icon={faPaperPlane}
                                            className="me-2 mt-1 text-muted"
                                            style={{ width: '20px' }}
                                        />
                                        <div>
                                            <small className="text-muted d-block">Email de l'émetteur</small>
                                            <span>{transfer.issuerEmail}</span>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-start mb-2">
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className="me-2 mt-1 text-muted"
                                            style={{ width: '20px' }}
                                        />
                                        <div>
                                            <small className="text-muted d-block">Destinataire</small>
                                            <span>
                                                {transfer.recipientDetails.firstName} {transfer.recipientDetails.lastName}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-start mb-2">
                                        <FontAwesomeIcon
                                            icon={faEnvelope}
                                            className="me-2 mt-1 text-muted"
                                            style={{ width: '20px' }}
                                        />
                                        <div>
                                            <small className="text-muted d-block">Pays du destinataire</small>
                                            <span>{transfer.recipientDetails.country}</span>
                                        </div>
                                    </div>
                                </div>



                                {/* Reference */}
                                <div className="bg-light p-3 rounded mb-3">
                                    <small className="text-muted d-block mb-1">Référence</small>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <code>{transfer.referenceNumber}</code>

                                    </div>
                                </div>

                                {/* Action buttons (admin only) */}
                                <RoleBasedRender roles={['ADMIN']}>
                                    <div className="d-flex gap-2">
                                        <button
                                            className={`btn btn-success flex-grow-1 ${loadingButton === transfer.id ? 'disabled' : ''}`}
                                            onClick={(e) => handleValidateTransfer(e, transfer)}
                                            disabled={loadingButton === transfer.id || showRejectModal}
                                        >
                                            {loadingButton === transfer.id ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Validation...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                                                    Valider
                                                </>
                                            )}
                                        </button>
                                        <button
                                            className="btn btn-danger flex-grow-1"
                                            onClick={(e) => handleRejectTransfer(e, transfer)}
                                            disabled={loadingButton === transfer.id || showRejectModal}
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="me-2" />
                                            Rejeter
                                        </button>
                                    </div>
                                </RoleBasedRender>
                            </div>
                        </div>
                    ))}
                </div>
            ))}

            {/* Reject Modal */}
            <Modal show={showRejectModal} onHide={handleCloseRejectModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Raison du rejet</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Veuillez saisir la raison du rejet :</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            style={{ minHeight: '120px' }}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseRejectModal}>
                        Annuler
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleRejectSubmit}
                        disabled={isSubmittingReject}
                    >
                        {isSubmittingReject ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Envoi en cours...
                            </>
                        ) : 'Envoyer'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TransferList;